import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ScrollView from 'devextreme-react/scroll-view';
import { TabPanel } from 'devextreme-react/tab-panel';
import { Toolbar, Item } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import { useMsal } from '@azure/msal-react';
import {
  CompetitionOverview,
  CompetitionStructureTab,
  CompetitionEntries,
  CompetitionResults,
  CompetitionAthletes,
  CompetitionRelays,
  CompetitionClubs,
  CompetitionCoaches,
  CompetitionOfficials,
  CompetitionRecords,
  CompetitionPermissions,
  CompetitionImportDialog,
  CompetitionExportDialog,
  ErrorDisplay
} from '../../components';
import { useSignalR } from '../../hooks/useSignalR';
import { getHubUrl } from '../../config/signalr-config';
import { getApiToken } from '../../utils/authUtils';
import './competition.scss';

const sectionIndex = {
  overview: 0,
  structure: 1,
  athletes: 2,
  relays: 3,
  clubs: 4,
  coaches: 5,
  officials: 6,
  entries: 7,
  results: 8,
  records: 9,
  permissions: 10
};

const entityConfig = {
  Competition: { stateKey: 'competition', idField: 'competitionId' },
  Session: { stateKey: 'sessions', idField: 'sessionId' },
  Event: { stateKey: 'events', idField: 'eventId' },
  Athlete: { stateKey: 'athletes', idField: 'athleteId' },
  Club: { stateKey: 'clubs', idField: 'clubId' },
  Coach: { stateKey: 'coaches', idField: 'coachId' },
  Official: { stateKey: 'officials', idField: 'officialId' },
  Heat: { stateKey: 'heats', idField: 'heatId' },
  CompetitionPermission: { stateKey: 'competitionPermissions', idField: 'competitionPermissionId' },
  Rankinggroup: { stateKey: 'rankinggroups', idField: 'rankinggroupId' },
  Ranking: { stateKey: 'rankings', idField: 'rankingId' },
  Recordlist: { stateKey: 'recordlists', idField: 'recordlistId' },
  Record: { stateKey: 'records', idField: 'recordId' },
  Result: { stateKey: 'results', idField: 'resultId' },
  Timestandard: { stateKey: 'timestandards', idField: 'timestandardId' }
};

const applyEntityChange = (prevData, entity, action, payload) => {
  if (!prevData) {
    return prevData;
  }

  const config = entityConfig[entity];
  if (!config) {
    return prevData;
  }

  const normalizedAction = (action || '').toLowerCase();
  const idField = config.idField;
  const getId = (item) => item?.[idField] || item?.id;
  const payloadId = getId(payload);

  if (config.stateKey === 'competition') {
    if (normalizedAction === 'delete') {
      return prevData;
    }
    return {
      ...prevData,
      competition: {
        ...prevData.competition,
        ...payload
      }
    };
  }

  const currentItems = prevData[config.stateKey] || [];

  if (normalizedAction === 'delete') {
    return {
      ...prevData,
      [config.stateKey]: currentItems.filter((item) => getId(item) !== payloadId)
    };
  }

  if (normalizedAction === 'insert') {
    const exists = currentItems.some((item) => getId(item) === payloadId);
    return {
      ...prevData,
      [config.stateKey]: exists
        ? currentItems.map((item) => (getId(item) === payloadId ? { ...item, ...payload } : item))
        : [...currentItems, payload]
    };
  }

  if (normalizedAction === 'update') {
    return {
      ...prevData,
      [config.stateKey]: currentItems.map((item) => (getId(item) === payloadId ? { ...item, ...payload } : item))
    };
  }

  return prevData;
};

export default function CompetitionManagePage() {
  const { competitionId, section } = useParams();
  const navigate = useNavigate();
  const { instance: msalInstance, accounts } = useMsal();

  const initialTab = useMemo(() => sectionIndex[section] ?? 0, [section]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(initialTab);
  const [accessToken, setAccessToken] = useState(null);
  
  // State für geladene Daten
  const [competitionData, setCompetitionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  // Import/Export Dialog States
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  
  // Token holen
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getApiToken(msalInstance, accounts);
        setAccessToken(token);
        console.log('Token für SignalR erhalten:', token ? 'Token verfügbar' : 'Kein Token');
      } catch (error) {
        console.error('Fehler beim Abrufen des Tokens:', error);
      }
    };

    if (msalInstance && accounts && accounts.length > 0) {
      fetchToken();
    }
  }, [msalInstance, accounts]);

  // SignalR Hook
  const hubUrl = useMemo(() => getHubUrl('competition'), []);
  const { isConnected, invokeMethod, onMessage, offMessage } = useSignalR(hubUrl, accessToken);

  const handleEntityChange = useCallback((entity, action, payload) => {
    setCompetitionData((prev) => applyEntityChange(prev, entity, action, payload));
  }, []);

  // Daten laden
  useEffect(() => {
    if (isConnected && competitionId) {
      setIsLoading(true);
      setLoadError(null);

      invokeMethod('LoadCompetition', competitionId)
        .then((data) => {
          console.log('Competition data loaded:', data);
          setCompetitionData(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error loading competition:', error);
          // Nur HubException-Nachricht extrahieren
          let errorMessage = 'Fehler beim Laden der Wettkampfdaten';
          if (error) {
            let fullError = '';
            if (typeof error === 'string') {
              fullError = error;
            } else if (error.message) {
              fullError = error.message;
            } else if (error.toString) {
              fullError = error.toString();
            }
            
            // Extrahiere nur die HubException-Nachricht
            const hubExceptionMatch = fullError.match(/HubException:\s*(.+)/);
            if (hubExceptionMatch) {
              errorMessage = hubExceptionMatch[1].trim();
            } else {
              errorMessage = fullError;
            }
          }
          setLoadError(errorMessage);
          setIsLoading(false);
        });
    }

    // Cleanup: UnloadCompetition aufrufen beim Unmount
    return () => {
      if (isConnected && competitionId) {
        invokeMethod('UnloadCompetition', competitionId)
          .catch((error) => console.error('Error unloading competition:', error));
      }
    };
  }, [isConnected, competitionId, invokeMethod]);

  // Live-Updates aus SignalR in lokalen State spiegeln
  useEffect(() => {
    if (!isConnected) {
      return undefined;
    }

    const handler = (message) => {
      if (!message || message.competitionId !== competitionId) {
        return;
      }
      handleEntityChange(message.entity, message.action, message.payload);
    };

    onMessage('competitionEntityChanged', handler);
    return () => offMessage('competitionEntityChanged');
  }, [isConnected, competitionId, onMessage, offMessage, handleEntityChange]);

  // Tab-spezifische Daten vorbereiten
  const tabsData = useMemo(() => ([
    { key: 'overview', title: 'Allgemein', content: <CompetitionOverview competitionId={competitionId} data={competitionData?.competition} /> },
    { key: 'structure', title: 'Struktur', content: <CompetitionStructureTab competitionId={competitionId} sessions={competitionData?.sessions} events={competitionData?.events} heats={competitionData?.heats} rankings={competitionData?.rankings} rankinggroups={competitionData?.rankinggroups} results={competitionData?.results} athletes={competitionData?.athletes} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'athletes', title: 'Athleten', content: <CompetitionAthletes competitionId={competitionId} data={competitionData?.athletes} clubs={competitionData?.clubs} results={competitionData?.results} heats={competitionData?.heats} events={competitionData?.events} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'relays', title: 'Staffeln', content: <CompetitionRelays competitionId={competitionId} data={competitionData?.athletes} clubs={competitionData?.clubs} results={competitionData?.results} heats={competitionData?.heats} events={competitionData?.events} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'clubs', title: 'Vereine', content: <CompetitionClubs competitionId={competitionId} data={competitionData?.clubs} athletes={competitionData?.athletes} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'coaches', title: 'Trainer', content: <CompetitionCoaches competitionId={competitionId} data={competitionData?.coaches} clubs={competitionData?.clubs} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'officials', title: 'Offizielle', content: <CompetitionOfficials competitionId={competitionId} data={competitionData?.officials} sessions={competitionData?.sessions} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'entries', title: 'Meldungen', content: <CompetitionEntries competitionId={competitionId} data={competitionData} /> },
    { key: 'results', title: 'Ergebnisse', content: <CompetitionResults competitionId={competitionId} data={competitionData} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'records', title: 'Rekorde', content: <CompetitionRecords competitionId={competitionId} data={competitionData?.records} recordlists={competitionData?.recordlists} accessToken={accessToken} onEntityChange={handleEntityChange} /> },
    { key: 'permissions', title: 'Berechtigungen', content: <CompetitionPermissions competitionId={competitionId} data={competitionData?.competitionPermissions} users={competitionData?.users} accessToken={accessToken} onEntityChange={handleEntityChange} /> }
  ]), [competitionId, competitionData, accessToken, handleEntityChange]);

  const onTabSelectionChanged = useCallback((e) => {
    setSelectedTabIndex(e.selectedIndex);
  }, []);

  const onBack = useCallback(() => navigate(-1), [navigate]);

  const onRefresh = useCallback(() => {
    if (isConnected && competitionId) {
      setIsLoading(true);
      setLoadError(null);

      invokeMethod('LoadCompetition', competitionId)
        .then((data) => {
          console.log('Competition data refreshed:', data);
          setCompetitionData(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error refreshing competition:', error);
          // Nur HubException-Nachricht extrahieren
          let errorMessage = 'Fehler beim Aktualisieren der Daten';
          if (error) {
            let fullError = '';
            if (typeof error === 'string') {
              fullError = error;
            } else if (error.message) {
              fullError = error.message;
            } else if (error.toString) {
              fullError = error.toString();
            }
            
            // Extrahiere nur die HubException-Nachricht
            const hubExceptionMatch = fullError.match(/HubException:\s*(.+)/);
            if (hubExceptionMatch) {
              errorMessage = hubExceptionMatch[1].trim();
            } else {
              errorMessage = fullError;
            }
          }
          setLoadError(errorMessage);
          setIsLoading(false);
        });
    }
  }, [isConnected, competitionId, invokeMethod]);

  const handleImportComplete = useCallback((result) => {
    // Nach erfolgreichem Import die Daten neu laden
    onRefresh();
  }, [onRefresh]);

  const handleShowImportDialog = useCallback(() => {
    setImportDialogVisible(true);
  }, []);

  const handleShowExportDialog = useCallback(() => {
    setExportDialogVisible(true);
  }, []);

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-competition-details'>
        <Toolbar className='toolbar-details theme-dependent'>
          <Item location='before'>
            <Button icon='arrowleft' stylingMode='text' onClick={onBack} />
          </Item>
          <Item location='before'>
            <div className='toolbar-title'>
              Wettkampf {competitionData?.competition?.competitionName || competitionId || 'unbekannt'}
              {isLoading && <span> (wird geladen...)</span>}
            </div>
          </Item>
          <Item location='after' locateInMenu='auto'>
            <Button 
              text='Refresh' 
              icon='refresh' 
              stylingMode='text' 
              onClick={onRefresh}
              disabled={!isConnected || isLoading}
            />
          </Item>
          <Item location='after' locateInMenu='auto'>
            <Button 
              text='Import' 
              icon='upload' 
              stylingMode='text' 
              onClick={handleShowImportDialog}
              disabled={!accessToken}
            />
          </Item>
          <Item location='after' locateInMenu='auto'>
            <Button 
              text='Export' 
              icon='download' 
              stylingMode='text' 
              onClick={handleShowExportDialog}
              disabled={!accessToken || !competitionData}
            />
          </Item>
        </Toolbar>

        {loadError ? (
          <ErrorDisplay 
            error={loadError} 
            title='Wettkampf konnte nicht geladen werden'
          />
        ) : (
          <div className='dx-card details-card competition-tabs-card'>
            {isLoading ? (
              <div className='loading-indicator'>Wettkampfdaten werden geladen...</div>
            ) : (
              <TabPanel
                dataSource={tabsData}
                selectedIndex={selectedTabIndex}
                onSelectionChanged={onTabSelectionChanged}
                height='100%'
                showNavButtons
                swipeEnabled={false}
                keyExpr='key'
                itemTitleRender={(item) => item.title}
                itemRender={(item) => (
                  <div className='tab-content'>{item.content}</div>
                )}
              />
            )}
          </div>
        )}

        {/* Import/Export Dialogs */}
        <CompetitionImportDialog
          visible={importDialogVisible}
          onHiding={() => setImportDialogVisible(false)}
          onImportComplete={handleImportComplete}
          token={accessToken}
          competitionId={competitionId}
        />
        
        {/* <CompetitionExportDialog
          visible={exportDialogVisible}
          onHiding={() => setExportDialogVisible(false)}
          competitionId={competitionId}
          competitionName={competitionData?.competition?.competitionName}
          competitionData={competitionData}
          token={accessToken}
        /> */}
      </div>
    </ScrollView>
  );
}
